import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConfigProvider, Layout } from 'antd';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  getCurrentUser,
  isUserAuthenticated,
} from './utils/localStorageManager';
import { GlobalTheme } from './theme/GlobalTheme';
import { darkTheme, lightTheme } from './theme/themeConfig';
import { store } from './store/store';
import PrivateRoutes from './routes/PrivateRoutes';
import { CommonData } from './routes/AuthContext';
import Settings from './pages/Settings';
import {
  AccountSettings,
  AppPreference,
  FAQ,
  Feedback,
  NotificationSettings,
  PasswordManagement,
  PrivacyPolicies,
  TermsConditions,
} from './components/Settings';
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AccountVerification = lazy(() => import('./pages/AccountVerification'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Home = lazy(() => import('./pages/Home'));
const OrganizationRegister = lazy(() => import('./pages/OrganizationRegister'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const App = () => {
  const [isDark, toggleIsDark] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUserData = () => {
    const currentUser = getCurrentUser();
    setUserData(currentUser);
    setIsLoading(false);
  };

  useEffect(() => {
    if (isUserAuthenticated()) {
      refreshUserData();
    } else {
      setIsLoading(false);
    }
  }, []);

  const currentTheme = useMemo(
    () => (isDark ? darkTheme : lightTheme),
    [isDark]
  );

  return (
    <Provider store={store}>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <GlobalTheme
          isDark={isDark}
          toggleIsDark={toggleIsDark}
          theme={currentTheme}
        >
          <ConfigProvider theme={currentTheme}>
            <Layout>
              <Layout className="site-wrapper">
                <Router>
                  <CommonData userData={userData} refreshUserData={refreshUserData}>
                  {isLoading ? (
                    <div>Loading...</div>
                  ) : !userData ? (
                    <Suspense>
                      <Routes>
                        <Route path="/auth/login" element={<Login />} />
                        {/* <Route path="/auth/register" element={<Register />} /> */}
                        <Route
                          path="/auth/account-verification"
                          element={<AccountVerification />}
                        />
                        <Route
                          path="/auth/reset-password"
                          element={<ResetPassword />}
                        />
                        <Route
                          path="/auth/forgot-password"
                          element={<ForgotPassword />}
                        />
                        <Route
                          path="*"
                          element={<Navigate to="/auth/login" replace />}
                        />
                      </Routes>
                    </Suspense>
                  ) : (
                    <Suspense>
                      <Routes>
                        <Route element={<PrivateRoutes />}>
                          <Route path="/" element={<Home />}>
                            <Route path="/" element={<AdminSettings />} />

                          </Route>
                          <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                          />
                          <Route
                            path="/gather-requirements"
                            element={<OrganizationRegister />}
                          />
                          <Route path="/settings" element={<Settings />} />
                          <Route
                            path="/settings/account-settings"
                            element={<AccountSettings />}
                          />
                          <Route
                            path="/settings/account-settings/password-management"
                            element={<PasswordManagement />}
                          />
                          <Route
                            path="/settings/notifications-settings"
                            element={<NotificationSettings />}
                          />
                          <Route path="/settings/faq" element={<FAQ />} />
                          <Route
                            path="/settings/feedback"
                            element={<Feedback />}
                          />
                          <Route
                            path="/settings/app-preference"
                            element={<AppPreference />}
                          />
                          <Route
                            path="/settings/privacy-policy"
                            element={<PrivacyPolicies />}
                          />
                          <Route
                            path="/settings/terms-&-conditions"
                            element={<TermsConditions />}
                          />
                          <Route
                            path="/edit-profile"
                            element={<EditProfile />}
                          />
                        </Route>

                      </Routes>
                    </Suspense>
                  )}
                  </CommonData>
                </Router>
              </Layout>
            </Layout>
          </ConfigProvider>
        </GlobalTheme>
      </GoogleOAuthProvider>
    </Provider>
  );
};

export default App;
